/**
 * useMealRepetition Hook
 * 
 * Manages state and logic for repeating meals across multiple days,
 * including overlap detection and conflict resolution.
 */

import { useState, useCallback, useMemo } from 'react';
import {
    generateRepeatedMeals,
    detectOverlaps,
    validateSelections,
    processClears,
    isSelectionLeftover,
    RepetitionStrategy
} from '@utils/MealRepetition';
import { getPrimaryMeal } from '@utils/Meals';

export function useMealRepetition({
    sourceMeals,
    existingPlan,
    profile,
    recipes,
    foods,
    onComplete,
    onError = console.error
}) {
    // Core state
    const [selections, setSelections] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);

    // Derived state from source meals
    const { primary, titles } = useMemo(() => {
        if (!sourceMeals || !sourceMeals.length) {
            return { primary: null, titles: [] };
        }
        return getPrimaryMeal(sourceMeals, recipes, foods);
    }, [sourceMeals, recipes, foods]);

    const idsToRepeat = useMemo(() => {
        return sourceMeals ? sourceMeals.map(m => m.id) : [];
    }, [sourceMeals]);

    const ogOffset = primary?.offset;
    const ogMealType = primary?.meal;

    // Calculate original leftover offsets
    const ogLeftoverOffsets = useMemo(() => {
        if (!existingPlan?.items || !idsToRepeat.length) return [];
        
        const ogLeftovers = existingPlan.items.filter(m => 
            !m.deleted && idsToRepeat.includes(m.parent)
        );
        
        let offsets = ogLeftovers.map(m => m.offset);
        // Remove duplicates
        return offsets.filter((offset, index) => offsets.indexOf(offset) === index);
    }, [existingPlan, idsToRepeat]);

    /**
     * Toggle selection of a meal type/offset combination
     */
    const toggleSelection = useCallback((offset, mealType) => {
        setSelections(prev => {
            const existingIndex = prev.findIndex(sel => 
                sel.offset === offset && sel.mealType === mealType
            );

            if (existingIndex >= 0) {
                // Remove existing selection
                return prev.filter((_, index) => index !== existingIndex);
            } else {
                // Add new selection
                return [...prev, { offset, mealType }];
            }
        });
        
        // Clear any existing error when user changes selection
        if (error) {
            setError(null);
        }
    }, [error]);

    /**
     * Check if a meal type/offset is selected
     */
    const isSelected = useCallback((offset, mealType) => {
        return selections.some(sel => sel.offset === offset && sel.mealType === mealType);
    }, [selections]);

    /**
     * Check if a meal type/offset is the original meal
     */
    const isOriginalMeal = useCallback((offset, mealType) => {
        return offset === ogOffset && mealType === ogMealType;
    }, [ogOffset, ogMealType]);

    /**
     * Check if a meal type/offset is disabled (original meal, leftover, etc.)
     */
    const isDisabled = useCallback((offset, mealType) => {
        // Can't select the original meal
        if (isOriginalMeal(offset, mealType)) {
            return true;
        }

        // Can't select existing leftover slots
        if (!existingPlan?.items) return false;
        
        const mealTypeDateMeals = existingPlan.items.filter(m => 
            m.meal === mealType && m.offset === offset
        );
        const isExistingLeftover = mealTypeDateMeals.some(m => 
            idsToRepeat.includes(m.parent)
        );
        
        if (isExistingLeftover) {
            return true;
        }

        // Can't select slots that would be leftovers from other selections
        return isSelectionLeftover(offset, mealType, selections, ogLeftoverOffsets);
    }, [isOriginalMeal, existingPlan, idsToRepeat, selections, ogLeftoverOffsets]);

    /**
     * Clear all selections
     */
    const clearSelections = useCallback(() => {
        setSelections([]);
        setError(null);
    }, []);

    /**
     * Validate current selections
     */
    const validationErrors = useMemo(() => {
        if (!selections.length) return [];
        
        return validateSelections(selections, ogOffset, ogMealType, ogLeftoverOffsets);
    }, [selections, ogOffset, ogMealType, ogLeftoverOffsets]);

    /**
     * Generate repeated meals and detect overlaps
     */
    const { repeatedMeals, overlaps, clears } = useMemo(() => {
        if (!selections.length || !sourceMeals || !profile) {
            return { repeatedMeals: [], overlaps: [], clears: [] };
        }

        const repeated = generateRepeatedMeals(sourceMeals, selections, profile, recipes);
        const { overlaps: detectedOverlaps, clears: detectedClears } = detectOverlaps(
            repeated, 
            existingPlan, 
            idsToRepeat
        );

        return {
            repeatedMeals: repeated,
            overlaps: detectedOverlaps,
            clears: detectedClears
        };
    }, [selections, sourceMeals, profile, recipes, existingPlan, idsToRepeat]);

    /**
     * Process repetition with conflict resolution if needed
     */
    const processRepetition = useCallback(async (resolutionStrategy = RepetitionStrategy.REPLACE) => {
        if (!selections.length) {
            setError('Please select where you\'d like to repeat this meal');
            return false;
        }

        if (validationErrors.length > 0) {
            setError(validationErrors[0]);
            return false;
        }

        setIsProcessing(true);
        setError(null);

        try {
            let mealsToProcess;

            if (overlaps.length === 0) {
                // No conflicts, process all clears and complete
                mealsToProcess = processClears(clears);
                await onComplete(mealsToProcess, { overlaps, clears });
                return { hasOverlaps: false, overlaps, clears };
            } else {
                // Handle conflicts based on strategy
                if (resolutionStrategy === RepetitionStrategy.CANCEL) {
                    setIsProcessing(false);
                    return { hasOverlaps: true, overlaps, clears };
                }

                // Don't call onComplete - conflict resolution will be handled by modal
                setIsProcessing(false);
                return { hasOverlaps: true, overlaps, clears };
            }

        } catch (err) {
            setError(err.message || 'Failed to repeat meals');
            onError(err);
            return false;
        } finally {
            setIsProcessing(false);
        }
    }, [selections, validationErrors, overlaps, clears, onComplete, onError]);

    /**
     * Complete repetition after conflict resolution
     */
    const completeRepetition = useCallback(async (resolvedMeals) => {
        setIsProcessing(true);
        
        try {
            await onComplete(resolvedMeals, { overlaps: [], clears: [] });
            return true;
        } catch (err) {
            setError(err.message || 'Failed to complete meal repetition');
            onError(err);
            return false;
        } finally {
            setIsProcessing(false);
        }
    }, [onComplete, onError]);

    return {
        // State
        selections,
        error,
        isProcessing,
        
        // Derived data
        primary,
        titles,
        ogOffset,
        ogMealType,
        idsToRepeat,
        overlaps,
        clears,
        validationErrors,
        hasSelections: selections.length > 0,
        hasOverlaps: overlaps.length > 0,

        // Actions
        toggleSelection,
        clearSelections,
        processRepetition,
        completeRepetition,

        // Helpers
        isSelected,
        isOriginalMeal,
        isDisabled
    };
}